import { useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { ToolTip } from '../../../components/toolTip/ToolTip'
import { translations } from '../../../configs/translations'
import { RaceTypes } from '../../../enums/RaceTypes'
import { RatingResponse } from '../../../models/response/RatingResponse'
import { UserProfile } from '../../../models/UserProfile'
import AccomplishmentsService from '../../../services/AccomplishmentsService'
import { useStore } from '../../../store/store'
import { Header } from '../../main/header/Header'
import { MainRating } from './mainRating/MainRating'
import { RanksTip } from './ranksTip/RanksTip'
import './Rating.css'
import { RatingAchievement } from './ratingAchievement/RatingAchievement'

export const Rating = () => {

	const [userProfile, setUserProfile] = useState<UserProfile | null>(JSON.parse(localStorage.getItem('profile') || 'null'))
	const language = useStore(state => state.language) as keyof typeof translations
	const t = translations[language]

	const [activeIndex, setActiveIndex] = useState<number>(1)
	const [mouseX, setMouseX] = useState<number>(0)
	const [mouseY, setMouseY] = useState<number>(0)
	const [isShowTooltip, setIsShowTooltip] = useState<boolean>(false)

	const [ratingData, setRatingData] = useState<RatingResponse | null>(null)
	const isRequestSent = useRef<boolean>(false)
	const [activeTab, setActiveTab] = useState<string>(t.race)
	const [isHovered, setIsHovered] = useState<boolean>(false)

	useEffect(() => {
		const fetchData = async () => {
			if (isRequestSent.current) return
			isRequestSent.current = true
			await AccomplishmentsService.getRating()
				.then((response) => {
					setRatingData(response)
				})
				.catch((error) => {
					toast.error(
						error && error.response && error.response.data && t[error.response.data as keyof typeof t]
							? t[error.response.data as keyof typeof t]
							: t.error_unknown,
						{ className: 'notification-error' }
					)
				})
			isRequestSent.current = false
		}
		fetchData()
	}, [])

	const [items, setItems] = useState<{ rating: number; level: number; name: string }[]>([])
	const [activeTotalRating, setActiveTotalRating] = useState<number>(0)

	useEffect(() => {
		const data = ratingData?.race
		setItems([
			{ rating: data?.ratings[0].rank || 1, level: data?.ratings[0].level || 0, name: 'VI' },
			{ rating: data?.ratings[1].rank || 1, level: data?.ratings[1].level || 0, name: 'VIII' },
			{ rating: data?.ratings[2].rank || 1, level: data?.ratings[2].level || 0, name: 'X' },
		])
		setActiveTotalRating(data?.ratings[0].totalRating || 0)
	}, [activeTab, ratingData])

	useEffect(() => {
		const audio = new Audio('/sounds/menu_sound_1.wav')
		audio.volume = 0.1
		audio.play()
	}, [activeIndex])

	const getPositionClass = (index: number) => {
		if (index === activeIndex) return 'rank-center'
		const leftIndex = (activeIndex - 1 + items.length) % items.length
		const rightIndex = (activeIndex + 1) % items.length
		if (index === leftIndex) return 'rank-left'
		if (index === rightIndex) return 'rank-right'
		return ''
	}

	const handleItemClick = (index: number) => {
		if (index === activeIndex) return
		setActiveIndex(index)
		setActiveTotalRating(ratingData?.race.ratings[index].totalRating || 0)
	}

	const handleMouseEnter = (event: React.MouseEvent) => {
		setMouseX(event.clientX)
		setMouseY(event.clientY)
		setIsHovered(true)
	}

	const handleMouseLeave = () => {
		setIsHovered(false)
	}

	useEffect(() => {
		if (isHovered) {
			setTimeout(() => setIsShowTooltip(true), 500)
		} else {
			setIsShowTooltip(false)
		}
	}, [isHovered])

	const [rankList, setRankList] = useState<{ rank: number; name: string, points: number, isHighlited: boolean }[]>([])
	const [rank, setRank] = useState<{ rank: number; level: number; name: string } | null>(null)
	const [pointsToNext, setPointsToNext] = useState<number>(0)

	useEffect(() => {
		const data = ratingData?.race
		const rankData = data?.ratings[activeIndex]
		setRank({
			rank: rankData?.rank || 1,
			level: rankData?.level || 0,
			name: t[rankData?.name as keyof typeof t] || t.unknown_rank
		})
		setPointsToNext(rankData?.experiencePointsToNextLevel || 0)

		const getRankList = (rankList: { [key: string]: number }) => {
			let i = 1
			return Object.entries(rankList)
				.sort((a, b) => a[1] - b[1])
				.map(([rank, points]) => ({
					rank: i++,
					name: t[rank as keyof typeof t] || t.unknown_rank,
					points,
					isHighlited: rank === rankData?.name
				}))
				.reverse()
		}
		setRankList(getRankList(ratingData?.rankList || {}))
	}, [activeTab, ratingData, activeIndex])

	return (
		<>
			{ratingData &&
				<div>
					<Header
						profile={userProfile}
						setProfile={setUserProfile}
						selectedMode={RaceTypes.RANKED}
						selectedVehicle={null}
						title={t.rating}
					/>

					<div className='rating-content-wrapper'>

						<div className='rating-content-zone-wrapper'>

							<div className='rating-content-zone'>
								<div className='rating-content-item additional'>
									<RatingAchievement
										type={'MATCHES'}
										amount={ratingData.race.matchesPlayed}
									/>
								</div>

								<div className='rating-content-item main'>
									<div className='rating-rank-wrapper'>
										<div className='rating-ranks-list'>
											{items.map((item, idx) => (
												<div
													key={item.name + idx}
													className={`rating-rank-item ${getPositionClass(idx)}`}
													onClick={() => handleItemClick(idx)}
													onMouseEnter={handleMouseEnter}
													onMouseMove={handleMouseEnter}
													onMouseLeave={handleMouseLeave}
													style={{ cursor: idx === activeIndex ? 'default' : 'pointer' }}
												>
													<MainRating withoutUnderlying={idx != activeIndex} rating={item.rating} level={item.level} name={item.name} />
												</div>
											))}
										</div>
										<div className='rating-text'>
											<p>{activeTotalRating}</p>
										</div>
									</div>
								</div>

								<div className='rating-content-item additional'>
									<RatingAchievement
										type={'WINS'}
										amount={ratingData.race.victories}
									/>
								</div>
							</div>
						</div>
						<ToolTip
							style='left'
							position={{ x: mouseX, y: mouseY }}
							content={<RanksTip
								rank={rank?.rank || 0}
								level={rank?.level || 0}
								name={rank?.name || ''}
								pointsToNext={pointsToNext}
								allRanks={rankList}
							/>}
							isShown={isHovered && isShowTooltip}
						/>
					</div>
				</div>
			}
			{!ratingData &&
				<div className='rating-no-races-title'>
					<p>{t.play_a_race_first}</p>
				</div>
			}
		</>
	)
}